import type { BillingInterval, BillingPlansData, PlanId, PlanOffer, PortalAccessLevel, UpgradeTargetPlan } from "../api/contract";

// Shared reading of the billing-plans endpoint. Three surfaces present the same
// offers — the upgrade banner, the Billing page's plan strip, and the Overview
// "Your plan" card — so the price strings, the yearly note, and the rule about who
// may be shown what live here once instead of drifting apart three ways.
//
// Nothing in this module hard-codes a price: every figure comes from the endpoint.

export type { UpgradeTargetPlan };
export type PlanOption = PlanOffer["options"][number];
export type CurrentPlan = PlanId | null;

/**
 * The most seats a Team subscription may hold. The plans endpoint does not yet carry a
 * per-offer `maxQuantity`, so the ceiling the checkout catalog publishes is mirrored
 * here until it does. A Pro account holding more than this cannot move to Team on its
 * own — it is routed to support (Enterprise, or reduce seats first).
 */
export const TEAM_SEAT_LIMIT = 50;

/**
 * Who may be shown what. Community sees every paid tier, Pro sees Team and Builders,
 * a legacy `consumer` (a Builders customer on an older Entitlements service) sees the
 * same, and every other level — team, builder, enterprise, and anything the contract
 * adds later — falls through to nothing rather than being offered a sideways or
 * backwards move.
 */
const ELIGIBLE_TARGETS: Partial<Record<PortalAccessLevel, UpgradeTargetPlan[]>> = {
  community: ["pro", "team", "builders"],
  pro: ["team", "builders"],
  consumer: ["team", "builders"]
};

/** Display names for the plan tokens the contract uses. */
const PLAN_DISPLAY_NAMES: Record<NonNullable<CurrentPlan>, string> = {
  community: "Community",
  pro: "Pro",
  team: "Team",
  builders: "Builders",
  enterprise: "Enterprise"
};

export interface OfferView {
  offer: PlanOffer;
  /** Monthly headline, formatted from the month option's amount and currency. */
  price: string;
  /** What the headline is a price of: "per seat / month" for Team, "per month" for Pro. */
  interval: string;
  /** The yearly alternative, when the API offers one, in the same unit as the headline. */
  yearlyNote: string | null;
}

export interface OfferContext {
  /**
   * Seats the account currently holds. A Pro account above the Team ceiling is not
   * offered Team — it needs support to move to Enterprise or to reduce seats first.
   */
  licensedSeats?: number | null;
  /**
   * The organization already holds an active Builders add-on. Its Builders benefits are
   * then already paid for, so the standalone Builders plan is not promoted on top of it.
   */
  buildersAddonActive?: boolean;
  /**
   * The Builders add-on is on sale to this organization. Builders is then bought as the
   * add-on on top of the current plan, never as a move to the standalone plan.
   */
  buildersAddonOnSale?: boolean;
}

export function optionFor(offer: PlanOffer, interval: PlanOption["interval"]): PlanOption | undefined {
  return offer.options.find((option) => option.interval === interval);
}

/** Whole amounts read as the site prices them ("$20"); part-dollar amounts keep cents. */
export function formatAmount(cents: number, currency: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    // An unrecognized currency code must not take the shell down with it.
    return `${new Intl.NumberFormat("en-US").format(amount)} ${currency.toUpperCase()}`;
  }
}

/**
 * The unit a plan's price is quoted in. Team is priced per named seat and Builders per
 * user; Pro is a single seat, so its price needs no unit. Keeping this in one place is
 * what makes "$40" unambiguous everywhere it is shown: the figure is always followed by
 * what it buys.
 */
export function priceUnit(plan: UpgradeTargetPlan | PlanId | string): string | null {
  if (plan === "team") return "seat";
  if (plan === "builders") return "user";
  return null;
}

/** "per seat / month", "per user / month", or "per month". */
export function intervalLabelFor(plan: UpgradeTargetPlan | PlanId | string, interval: BillingInterval): string {
  const unit = priceUnit(plan);
  const period = interval === "year" ? "year" : "month";
  return unit ? `per ${unit} / ${period}` : `per ${period}`;
}

/** "$40 per seat / month" — a catalog option's unit price with its unit spelled out. */
export function unitPriceLabel(plan: UpgradeTargetPlan | PlanId | string, option: PlanOption): string {
  return `${formatAmount(option.unitAmountCents, option.currency)} ${intervalLabelFor(plan, option.interval)}`;
}

export function yearlyNoteFor(offer: PlanOffer): string | null {
  const year = optionFor(offer, "year");
  if (!year) return null;
  const price = unitPriceLabel(offer.plan, year);
  return year.discountPercent > 0
    ? `Or ${price} — save ${year.discountPercent}%.`
    : `Or ${price}.`;
}

/**
 * What a seat count actually costs on one catalog option: "$200 per month for 5 seats".
 * Always the catalog unit amount times the real quantity — never a fixed total — and
 * null when the option, quantity, or currency cannot be trusted.
 */
export function seatTotalLabel(
  option: Pick<PlanOption, "unitAmountCents" | "currency" | "interval"> | null | undefined,
  seats: number | null | undefined
): string | null {
  if (!option || typeof seats !== "number" || !Number.isInteger(seats) || seats < 1) return null;
  if (!Number.isFinite(option.unitAmountCents) || option.unitAmountCents < 0) return null;
  const total = formatAmount(option.unitAmountCents * seats, option.currency);
  const period = option.interval === "year" ? "year" : "month";
  return `${total} per ${period} for ${new Intl.NumberFormat("en-US").format(seats)} ${seats === 1 ? "seat" : "seats"}`;
}

/**
 * The sentence that turns a per-seat Team price into what THIS account would pay:
 * "For your 5 seats: $200 per month, or $2,160 per year." Null when the offer carries no
 * monthly option or the seat count is unknown, so nothing invents a total.
 */
export function teamTotalsNote(offer: PlanOffer | null | undefined, seats: number | null | undefined): string | null {
  if (!offer || typeof seats !== "number" || !Number.isInteger(seats) || seats < 1) return null;
  const month = optionFor(offer, "month");
  if (!month) return null;
  const monthly = formatAmount(month.unitAmountCents * seats, month.currency);
  const year = optionFor(offer, "year");
  const count = new Intl.NumberFormat("en-US").format(seats);
  const noun = seats === 1 ? "seat" : "seats";
  if (!year) return `For your ${count} ${noun}: ${monthly} per month.`;
  const yearly = formatAmount(year.unitAmountCents * seats, year.currency);
  return `For your ${count} ${noun}: ${monthly} per month, or ${yearly} per year.`;
}

/**
 * The Team catalog entry the plans endpoint carries, wherever it carries it: as an
 * upgrade offer (a Pro or Community account) or as the current plan's own entry (a Team
 * account, when the endpoint publishes one). Null when the endpoint has not answered or
 * lists no Team plan — callers then fall back to copy that defers prices to Stripe.
 */
export function teamOfferFrom(data: Pick<BillingPlansData, "offers" | "current"> | null | undefined): PlanOffer | null {
  if (!data) return null;
  const offered = data.offers.find((offer) => offer.plan === "team");
  if (offered) return offered;
  return data.current?.plan === "team" ? data.current : null;
}

/** True when this account holds more seats than Team can carry. */
export function exceedsTeamSeatLimit(licensedSeats: number | null | undefined): boolean {
  return typeof licensedSeats === "number" && licensedSeats > TEAM_SEAT_LIMIT;
}

/**
 * The offers this account may actually buy, in endpoint order, each priced from its
 * monthly option. An offer without a monthly option is dropped rather than shown
 * unpriced, because every CTA built on this buys the monthly interval.
 */
export function offerViews(
  offers: PlanOffer[],
  currentAccessLevel: PortalAccessLevel | null,
  currentPlan: CurrentPlan,
  context: OfferContext = {}
): OfferView[] {
  if (!currentAccessLevel) return [];
  const targets = ELIGIBLE_TARGETS[currentAccessLevel] ?? [];
  const views: OfferView[] = [];
  for (const offer of offers) {
    if (!targets.includes(offer.plan) || offer.plan === currentPlan) continue;
    if (offer.plan === "team" && exceedsTeamSeatLimit(context.licensedSeats)) continue;
    if (offer.plan === "builders" && (context.buildersAddonActive === true || context.buildersAddonOnSale === true)) continue;
    const month = optionFor(offer, "month");
    if (!month) continue;
    views.push({
      offer,
      price: formatAmount(month.unitAmountCents, month.currency),
      interval: intervalLabelFor(offer.plan, "month"),
      yearlyNote: yearlyNoteFor(offer)
    });
  }
  return views;
}

/** The single next tier to promote where there is only room for one CTA. */
export function nextOfferView(
  offers: PlanOffer[],
  currentAccessLevel: PortalAccessLevel | null,
  currentPlan: CurrentPlan,
  context: OfferContext = {}
): OfferView | null {
  const views = offerViews(offers, currentAccessLevel, currentPlan, context);
  // Prefer the offer the endpoint marked featured; otherwise the first eligible one.
  return views.find((view) => view.offer.featured) ?? views[0] ?? null;
}

/** Customer-facing name for a plan token. Unknown tokens read as themselves. */
export function planDisplayName(plan: CurrentPlan | string): string {
  if (!plan) return "Community";
  return (PLAN_DISPLAY_NAMES as Record<string, string>)[plan] ?? plan;
}

/**
 * The access tier as a plan subtitle ("Builders access"), or null when it would only
 * repeat the plan name beside it ("Enterprise" over "enterprise access").
 */
export function accessLevelNote(
  plan: string | null | undefined,
  accessLevel: string | null | undefined
): string | null {
  if (!accessLevel) return null;
  const access = planDisplayName(accessLevel);
  if (plan && planDisplayName(plan).toLowerCase() === access.toLowerCase()) return null;
  return `${access} access`;
}

/**
 * The interval an upgrade should be created on. An existing subscriber keeps the
 * cadence they already pay on (a yearly Pro account moves to yearly Team); anyone
 * else — Community, or a summary that has not loaded — starts monthly, as the CTAs
 * are priced.
 */
export function upgradeIntervalFor(currentInterval: BillingInterval | null | undefined): BillingInterval {
  return currentInterval === "year" ? "year" : "month";
}

/**
 * The seat sentence shown beside a Team offer. A Pro → Team move preserves the seats
 * the account already pays for; a Community account starts with one and grows later.
 */
export function teamSeatNote(currentAccessLevel: PortalAccessLevel | null, licensedSeats: number | null | undefined): string | null {
  if (currentAccessLevel !== "pro" || typeof licensedSeats !== "number" || licensedSeats < 1) return null;
  const seats = new Intl.NumberFormat("en-US").format(licensedSeats);
  return `Your ${seats} ${licensedSeats === 1 ? "seat comes" : "seats come"} with you.`;
}

/**
 * Pages that present the offers themselves. The shell's ambient banner stands down on
 * these so one page never renders the identical plan cards twice.
 */
const BANNER_SUPPRESSED_ROUTES = new Set<string>(["billing"]);

export interface UpgradeBannerInput {
  canManageBilling: boolean;
  /** The viewer dismissed it; the 7-day snooze has not lapsed. */
  snoozed: boolean;
  /** Current route name, so a page that owns the offers can suppress the banner. */
  routeName: string;
  currentAccessLevel: PortalAccessLevel | null;
  currentPlan: CurrentPlan;
  offers: PlanOffer[];
  /** Seats currently held, so an oversized Pro account is not shown a Team it cannot buy. */
  licensedSeats?: number | null;
  /** An active Builders add-on: the standalone Builders plan is no longer promoted. */
  buildersAddonActive?: boolean;
  /** The add-on is on sale: Builders is sold as the add-on, not as the standalone plan. */
  buildersAddonOnSale?: boolean;
  /**
   * The Overview's Builders add-on notice is showing. Overview then carries one upsell,
   * not two stacked ones, so the banner stands down on that route only.
   */
  buildersAddonNoticeVisible?: boolean;
}

/**
 * Whether the shell should mount the upgrade banner. Kept out of AppShell so the rule
 * is unit-testable without mounting the whole shell, and so it decides on the SAME
 * eligible offers the banner would actually render rather than on the raw endpoint list.
 */
export function shouldShowUpgradeBanner(input: UpgradeBannerInput): boolean {
  if (!input.canManageBilling || input.snoozed) return false;
  if (BANNER_SUPPRESSED_ROUTES.has(input.routeName)) return false;
  if (input.routeName === "overview" && input.buildersAddonNoticeVisible === true) return false;
  return offerViews(input.offers, input.currentAccessLevel, input.currentPlan, {
    licensedSeats: input.licensedSeats ?? null,
    buildersAddonActive: input.buildersAddonActive === true,
    buildersAddonOnSale: input.buildersAddonOnSale === true
  }).length > 0;
}
